
import { useState } from 'react';

/* ── Badge ──────────────────────────────────────────────────────────── */
export function Badge({ children, color = 'gray' }) {
  const colors = {
    green:  { background: 'var(--green-dim)',  color: 'var(--green)'  },
    red:    { background: 'var(--red-dim)',    color: 'var(--red)'    },
    yellow: { background: 'var(--yellow-dim)', color: 'var(--yellow)' },
    blue:   { background: 'var(--accent-dim)', color: 'var(--accent)' },
    purple: { background: 'var(--purple-dim)', color: 'var(--purple)' },
    gray:   { background: 'rgba(100,110,130,0.15)', color: 'var(--text2)' },
  };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap', ...colors[color] }}>
      {children}
    </span>
  );
}

export function StatusBadge({ status }) {
  const map = { active:'green', success:'green', expired:'gray', cancelled:'red', failed:'red', pending:'yellow', inactive:'red' };
  return <Badge color={map[status] || 'gray'}>{status}</Badge>;
}

/* ── Button ─────────────────────────────────────────────────────────── */
export function Btn({ children, onClick, variant = 'primary', size = 'md', icon: Icon, disabled, loading, style = {}, type = 'button', title }) {
  const sizes = {
    xs: { padding: '4px 9px',  fontSize: 11, gap: 4,  iconSize: 11 },
    sm: { padding: '6px 13px', fontSize: 12, gap: 5,  iconSize: 13 },
    md: { padding: '9px 18px', fontSize: 13, gap: 6,  iconSize: 14 },
    lg: { padding: '11px 24px',fontSize: 14, gap: 7,  iconSize: 15 },
  };
  const variants = {
    primary: { background: 'var(--accent)', color: '#fff',        border: 'none' },
    danger:  { background: 'var(--red)',    color: '#fff',        border: 'none' },
    success: { background: 'var(--green)',  color: '#051a0d',     border: 'none' },
    warning: { background: 'var(--yellow)', color: '#1a0e00',     border: 'none' },
    ghost:   { background: 'transparent',   color: 'var(--text2)', border: '1px solid var(--border2)' },
    subtle:  { background: 'var(--surface2)', color: 'var(--text2)', border: '1px solid var(--border)' },
  };
  const s = sizes[size] || sizes.md;
  const v = variants[variant] || variants.primary;
  return (
    <button type={type} onClick={onClick} disabled={disabled || loading} title={title}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: s.gap, padding: s.padding, borderRadius: 8, fontSize: s.fontSize, fontWeight: 600, cursor: (disabled || loading) ? 'not-allowed' : 'pointer', opacity: (disabled || loading) ? 0.5 : 1, transition: 'all 0.15s', border: v.border, background: v.background, color: v.color, fontFamily: 'inherit', whiteSpace: 'nowrap', ...style }}>
      {loading && <span style={{ width: s.iconSize, height: s.iconSize, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite', display: 'inline-block', flexShrink: 0 }} />}
      {!loading && Icon && <Icon size={s.iconSize} />}
      {children}
    </button>
  );
}

/* ── Input ──────────────────────────────────────────────────────────── */
export function Input({ label, value, onChange, type = 'text', placeholder, required, error, hint, ...rest }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {label && <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}{required && <span style={{ color: 'var(--red)', marginLeft: 3 }}>*</span>}</label>}
      <input type={type} value={value ?? ''} placeholder={placeholder}
        onChange={e => onChange?.(e.target.value)}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        {...rest}
        style={{ padding: '10px 13px', borderRadius: 8, fontSize: 14, background: 'var(--surface2)', color: 'var(--text)', border: `1px solid ${error ? 'var(--red)' : focused ? 'var(--accent)' : 'var(--border2)'}`, outline: 'none', width: '100%', transition: 'border-color 0.15s' }}
      />
      {hint && !error && <span style={{ fontSize: 11, color: 'var(--text3)' }}>{hint}</span>}
      {error && <span style={{ fontSize: 11, color: 'var(--red)' }}>{error}</span>}
    </div>
  );
}

/* ── Select ─────────────────────────────────────────────────────────── */
export function Select({ label, value, onChange, options, required }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {label && <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}{required && <span style={{ color: 'var(--red)', marginLeft: 3 }}>*</span>}</label>}
      <select value={value} onChange={e => onChange(e.target.value)}
        style={{ padding: '10px 13px', borderRadius: 8, fontSize: 14, background: 'var(--surface2)', color: 'var(--text)', border: '1px solid var(--border2)', outline: 'none', cursor: 'pointer', width: '100%' }}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

/* ── Card ───────────────────────────────────────────────────────────── */
export function Card({ children, style = {}, className = '' }) {
  return (
    <div className={className} style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: 20, ...style }}>
      {children}
    </div>
  );
}

/* ── Modal ──────────────────────────────────────────────────────────── */
export function Modal({ title, children, onClose, width = 520 }) {
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 500, animation: 'fadeIn 0.15s ease', padding: '0' }}
      className="modal-backdrop">
      <div style={{ background: 'var(--surface)', width: '100%', maxWidth: width, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: 'var(--shadow)', animation: 'fadeUp 0.2s ease', borderRadius: '16px 16px 0 0' }}
        className="modal-inner">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 16, fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 22, lineHeight: 1, padding: '0 4px', borderRadius: 6 }}>×</button>
        </div>
        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>{children}</div>
      </div>
      <style>{`
        @media (min-width: 600px) {
          .modal-backdrop { align-items: center !important; padding: 16px !important; }
          .modal-inner { border-radius: 16px !important; }
        }
      `}</style>
    </div>
  );
}

/* ── Table (desktop) + Card list (mobile) ───────────────────────────── */
export function Table({ columns, rows, loading, emptyText = 'No data found.', mobileCard, onRowClick  }) {
  if (loading) return (
    <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text3)' }}>
      <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
        <span style={{ width: 14, height: 14, border: '2px solid var(--border2)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.6s linear infinite', display: 'inline-block' }} />
        Loading…
      </div>
    </div>
  );
  if (!rows.length) return (
    <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text3)', fontSize: 13 }}>{emptyText}</div>
  );

  return (
    <>
      <div className="table-desktop" style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              {columns.map(col => (
                <th key={col.key} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap' }}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.id || i}
                onClick={() => onRowClick?.(row)}                          // 👈
                style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.1s', cursor: onRowClick ? 'pointer' : 'default' }}  // 👈
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                {columns.map(col => (
                  <td key={col.key}
                    onClick={col.noNav ? e => e.stopPropagation() : undefined}  // 👈 stops action buttons from navigating
                    style={{ padding: '12px 14px', fontSize: 13, verticalAlign: 'middle', ...col.tdStyle }}>
                    {col.render ? col.render(row) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {mobileCard && (
        <div className="table-mobile" style={{ display: 'none', flexDirection: 'column', gap: 10, padding: '10px 14px' }}>
          {rows.map((row, i) => (
            <div key={row.id || i}
              onClick={() => onRowClick?.(row)}                            // 👈
              style={{ background: 'var(--surface2)', borderRadius: 10, padding: '12px 14px', border: '1px solid var(--border)', cursor: onRowClick ? 'pointer' : 'default' }}>
              {mobileCard(row)}
            </div>
          ))}
        </div>
      )}

      <style>{`
        @media (max-width: 640px) {
          .table-desktop { display: ${mobileCard ? 'none' : 'block'} !important; }
          .table-mobile  { display: ${mobileCard ? 'flex' : 'none'} !important; }
        }
      `}</style>
    </>
  );
}

/* ── Pagination ─────────────────────────────────────────────────────── */
export function Pagination({ page, total, limit = 20, onChange }) {
  const pages = Math.ceil(total / limit);
  if (pages <= 1) return null;
  const visiblePages = [];
  for (let p = Math.max(1, page - 2); p <= Math.min(pages, page + 2); p++) visiblePages.push(p);

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 4px', flexWrap: 'wrap', gap: 8 }}>
      <span style={{ fontSize: 12, color: 'var(--text3)' }}>
        {Math.min((page-1)*limit+1, total)}–{Math.min(page*limit, total)} of <b style={{ color: 'var(--text2)' }}>{total}</b>
      </span>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        <Btn size="sm" variant="ghost" disabled={page <= 1} onClick={() => onChange(page-1)}>← Prev</Btn>
        {visiblePages.map(p => (
          <button key={p} onClick={() => onChange(p)}
            style={{ width: 32, height: 32, borderRadius: 6, border: 'none', cursor: 'pointer', background: p===page ? 'var(--accent)' : 'transparent', color: p===page ? '#fff' : 'var(--text2)', fontSize: 13, fontFamily: 'inherit', fontWeight: p===page ? 700 : 400 }}>
            {p}
          </button>
        ))}
        <Btn size="sm" variant="ghost" disabled={page >= pages} onClick={() => onChange(page+1)}>Next →</Btn>
      </div>
    </div>
  );
}

/* ── Stat Card ──────────────────────────────────────────────────────── */
export function StatCard({ label, value, sub, accent, icon: Icon }) {
  return (
    <Card style={{ padding: '18px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>{label}</div>
          <div style={{ fontSize: 22, fontWeight: 800, fontFamily: 'Syne, sans-serif', color: accent || 'var(--text)', letterSpacing: '-0.02em', lineHeight: 1, wordBreak: 'break-all' }}>{value}</div>
          {sub && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 5 }}>{sub}</div>}
        </div>
        {Icon && (
          <div style={{ width: 38, height: 38, borderRadius: 10, background: accent ? `${accent}18` : 'var(--surface2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: accent || 'var(--text2)', flexShrink: 0, marginLeft: 10 }}>
            <Icon size={17} />
          </div>
        )}
      </div>
    </Card>
  );
}

/* ── Page Header ────────────────────────────────────────────────────── */
export function PageHeader({ title, subtitle, action }) {
  return (
    <div style={{ marginBottom: 22 }} className="page-header">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1 }}>{title}</h1>
          {subtitle && <p style={{ margin: '5px 0 0', color: 'var(--text2)', fontSize: 13 }}>{subtitle}</p>}
        </div>
        {action && <div style={{ flexShrink: 0 }}>{action}</div>}
      </div>
    </div>
  );
}

/* ── InfoAlert ──────────────────────────────────────────────────────── */
export function InfoAlert({ children, type = 'info' }) {
  const s = {
    info:    { bg: 'var(--accent-dim)',  border: 'rgba(79,142,247,0.2)',  color: 'var(--accent)'  },
    success: { bg: 'var(--green-dim)',   border: 'rgba(34,197,94,0.2)',   color: 'var(--green)'   },
    warning: { bg: 'var(--yellow-dim)',  border: 'rgba(245,158,11,0.2)',  color: 'var(--yellow)'  },
    danger:  { bg: 'var(--red-dim)',     border: 'rgba(239,68,68,0.2)',   color: 'var(--red)'     },
  }[type];
  return <div style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 8, padding: '10px 14px', fontSize: 13, color: s.color, lineHeight: 1.5 }}>{children}</div>;
}

/* ── StatsGrid ──────────────────────────────────────────────────────── */
export function StatsGrid({ children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
      {children}
    </div>
  );
}

/* ── FilterBar ──────────────────────────────────────────────────────── */
export function FilterBar({ children }) {
  return (
    <Card style={{ marginBottom: 14, padding: 12 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        {children}
      </div>
    </Card>
  );
}

/* ── MobileSelect (styled like native select but themed) ────────────── */
export function FilterSelect({ value, onChange, options }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border2)', background: 'var(--surface2)', color: 'var(--text)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', minWidth: 120 }}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

/* ── SearchInput ────────────────────────────────────────────────────── */
export function SearchInput({ value, onChange, placeholder = 'Search…' }) {
  return (
    <div style={{ position: 'relative', flex: 1, minWidth: 160 }}>
      <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', pointerEvents: 'none' }} width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z" /></svg>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: '100%', padding: '8px 12px 8px 30px', borderRadius: 8, border: '1px solid var(--border2)', background: 'var(--surface2)', color: 'var(--text)', fontSize: 13, outline: 'none', fontFamily: 'inherit' }} />
    </div>
  );
}

/* ── Row detail helper for modals ───────────────────────────────────── */
export function DetailRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '5px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
      <span style={{ color: 'var(--text3)', minWidth: 90, flexShrink: 0 }}>{label}</span>
      <span style={{ color: 'var(--text)', wordBreak: 'break-all' }}>{value}</span>
    </div>
  );
}
